'use client';

import { useEffect, useRef, useState } from 'react';

interface UseScrollRevealOptions {
  /** Visibility threshold (0-1). Default: 0.1 */
  threshold?: number;
  /** Once visible, stay visible. Default: true */
  triggerOnce?: boolean;
  /** Root margin for early/late triggering. Default: '0px 0px -50px 0px' */
  rootMargin?: string;
}

/**
 * Uses IntersectionObserver to detect when an element enters the viewport.
 * Designed for scroll-reveal animations — with triggerOnce (default),
 * the element stays visible once it enters the viewport.
 */
export function useScrollReveal({
  threshold = 0.1,
  triggerOnce = true,
  rootMargin = '0px 0px -50px 0px',
}: UseScrollRevealOptions = {}) {
  const ref = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (isVisible && triggerOnce) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, rootMargin, triggerOnce, isVisible]);

  return { ref, isVisible };
}
